import axios, { AxiosResponse, HeadersDefaults } from 'axios';
import * as Sentry from "@sentry/browser";

import { getRawToken } from './session'
import { getProjectId } from "./url-params"
import { ID, intboolean } from "../types/app-types"
import { FiltersFormType } from "products/types/filtertypes";
import { URL, GOD_MODE_ENABLED, GOD_MODE_COOKIE, INCOGNITO_MODE_COOKIE, APP_MAINTENANCE_MODE } from "app/config/api-config";

import Cookies from "universal-cookie"
import { DEFAULT_PRODUCTS_PER_PAGE, PreviewsForm } from "template-previews/types";
import ShapeType from "shape-entities/types/shape";
import {
    CustomImageSet,
    CustomImageSetFile,
    Size,
    SettingsFormType,
    CollageLayoutType,
    CustomPalettesFormType,
    Template,
    Gradient,
    Project,
    AlternativeTemplate,
} from "../types/model-types";

import { SetByUser } from "products/components/product-table";
import { ViewTableType } from "products/containers";
import { ProductWithoutElementFilter } from "diagnostics/elements-availability";
import { RandomProduct } from "shape-edit/types";
import { TemplateTypes } from "superadmin/components/project-info";
import { QueueActionType } from "superadmin/components/rabbit-queues";
import { ComparisonEngineType } from "diagnostics/diagnostics";
import { ObjectsType } from "products/components/watermark-marker-common";
import { WMProductItemType } from 'watermark-groups/watermark-groups-add-products';
import { CheckElementsType } from 'app/components/check-elements-modal';
import { PromoTemplate } from 'superadmin/components/audit-promo-templates';
import { WMResultsFilterType } from 'watermark-groups/watermark-groups-results';
import { MergadoFilterType } from 'superadmin/components/mergado-queries';
import { ApplyElementsType } from 'settings/components/advanced-project-settings';
import { InvertMaskModeType } from 'products/components/invert-mask-marker';
import { InpaintingGroupType, PositionType } from 'products/types';
import { FileInputType } from 'template-list/containers/template-list';
import { ProjectToProjectImportOption } from 'settings/components/import-from-project';
import { LevelsModeType } from 'template-edit/components/save-template-form';
import { TemplateOrAlternativeTemplateType } from 'template-edit/undoRedoReducers';

const cookies = new Cookies()

export const godProjectId = cookies.get(GOD_MODE_COOKIE)
export const isGodModeOn = window.location.pathname.indexOf(`project/${GOD_MODE_ENABLED}`) > -1
export const isLocalhost = window.location.hostname === 'localhost'
export const isIncognitoOn = cookies.get(INCOGNITO_MODE_COOKIE)     //no matter what value is set

export function isMobile(): boolean {
    return window.innerWidth <= 1024 || (typeof document !== 'undefined' && 'ontouchstart' in document)
}

export const PUBLIC_PATH = URL.publicPath

export const projectId: number = getProjectId() ? parseInt(getProjectId()) : parseInt(sessionStorage.getItem('project_id'))
if (projectId) {
    sessionStorage.setItem('project_id', projectId + '')
}

export const eshopId = 1
export const MERGADO_APP = 'https://app.mergado.com/projects/' + projectId + '/product'

// Add a request interceptor
axios.interceptors.request.use(function (config) {
    // Do something before request is sent
    let token = getRawToken()
    if (token) {
        if (config.headers) {
            config.headers.Authorization = `Bearer ${token}`
        }
    }

    if (APP_MAINTENANCE_MODE === '1') {
        console.debug('this is maintenance mode!!!', APP_MAINTENANCE_MODE)
        if (config.headers) {
            config.headers["X-MAINTENANCE-MODE"] = APP_MAINTENANCE_MODE;
        }
        return config;
    }

    if (isGodModeOn && godProjectId) {
        /*if(config.method !== "get" && config.url.indexOf('/image/apply') === -1) {
            if(!confirm("GOD MODE IS ON, opravdu provést akci?\nURL: " + config.url)) {
                return
            }
        }*/
        if (config.headers) {
            config.headers["X-GOD-MODE"] = godProjectId
        }
    }

    return config
}, function (error) {
    // Do something with request error
    return Promise.reject(error)
})

// Add a response interceptor
axios.interceptors.response.use(function (response) {
    // Do something with response data
    return response
}, function (error) {
    // Do something with response error
    /*console.error(error.response.data)*/
    if (error && error.response && error.response.status) {
        switch (error.response.status) {
            /*case 404:
                this.props.router.navigate( '/not-found' );
                break;*/
            case 401:
                //do not log this error
                break;
            case 500:
                Sentry.withScope((scope) => {
                    scope.setExtras(error.response.data)
                    Sentry.captureException(error)
                })
                /*this.props.router.navigate( '/server-error', error.response.data);*/
                break;
        }
    }
    return Promise.reject(error)
})

export function arrayToQueryString(obj, prefix) {
    let str = []
    const regexp = /(.*)\[([a-z]*)\]/
    for (const p in obj) {
        const search = regexp.exec(p)
        if (search && search.length > 2) {
            str.push((prefix ? prefix + "[" + search[1] + "]" : search[1]) + "[" + encodeURIComponent(search[2]) + "]=" + encodeURIComponent(obj[p]))
        } else if (obj.hasOwnProperty(p)) {
            const k = prefix ? prefix + "[" + p + "]" : p
            const v = typeof obj[p] === "number" ? `${obj[p]}` : obj[p]     //number to string conversion
            str.push((v !== null && typeof v === "object") ? arrayToQueryString(v, k) : encodeURIComponent(k) + "=" + (v ? encodeURIComponent(v) : ''))
        }
    }
    // remove empty elements
    return str.filter(n => n).join("&");
}

export function deleteFormat(sizeId) {
    return axios.delete(`/project/${projectId}/sizes/${sizeId}/delete`)
}

export function addFormat(newSize: Size) {
    return axios.put(`/project/${projectId}/sizes/new`, newSize)
}

export function editFormat(size: Size) {
    const sizeId = size?.id
    return axios.put(`/project/${projectId}/sizes/${sizeId}/edit`, size)
}

export function getCategories() {
    return axios.get(`/project/${projectId}/categories`)
}

export function getProjectCategories() {
    return axios.get(`/project/${projectId}/project-categories`)
}

export function saveProjectCategories(projectCategories: number[]) {
    return axios.put(`/project/${projectId}/save-project-categories`, { projectCategories })
}

export function saveWhoAreYou(whoAreYou: string) {
    return axios.put(`/project/${projectId}/save-who-are-you`, { whoAreYou })
}

export function getProjectCollageLayouts() {
    return axios.get(`/project/${projectId}/collage-layouts`)
}

export function saveCollageLayouts(collageLayouts: CollageLayoutType[]) {
    return axios.put(`/project/${projectId}/collage-layouts/save`, collageLayouts)
}

export function saveCollageLayout(collageLayout: CollageLayoutType, saveAsNew: boolean) {
    const collageLayoutId = collageLayout.id
    return axios.put(`/project/${projectId}/collage-layouts/${collageLayoutId}/save`, { collageLayout, saveAsNew })
}

export function exportCollageLayouts(collageLayoutIds: number[]) {
    return axios.post(`/project/${projectId}/collage-layouts/export`, { collageLayoutIds })
}

export function importCollageLayouts(base64: string, sourceName: string) {
    return axios.put(`/project/${projectId}/collage-layouts/import`, { base64, sourceName })
}

export function createDefaultLayouts() {
    return axios.put(`/project/${projectId}/collage-layouts/create-defaults`)
}

export function deleteAllUnused() {
    return axios.delete(`/project/${projectId}/collage-layouts/delete-all-unused`)
}

export function deleteCollageLayout(collageLayoutId: ID) {
    return axios.delete(`/project/${projectId}/collage-layouts/${collageLayoutId}/delete`)
}

export function deleteCollageLayoutImage(collageLayoutId: ID, collageLayoutImageId: ID) {
    return axios.delete(`/project/${projectId}/collage-layouts/${collageLayoutId}/image/${collageLayoutImageId}/delete`)
}

export function addCollageLayout() {
    return axios.put(`/project/${projectId}/collage-layouts/new`)
}

export function addCollageLayoutImage(collageLayout: CollageLayoutType) {
    return axios.put(`/project/${projectId}/collage-layouts/${collageLayout.id}/add-image`, collageLayout)
}

export function getProjectSettings() {
    return axios.get(`/project/${projectId}/settings`)
}

export function superadminGetProjects() {
    return axios.get(`/project/${projectId}/superadmin`)
}

export function superadminGetTasksPerProject() {
    return axios.get(`/project/${projectId}/superadmin/get-tasks-per-project`);
}

export function superadminGetProductsPerProject() {
    return axios.get(`/project/${projectId}/superadmin/get-products-per-project`);
}

export function superadminGetProjectsHistory() {
    return axios.get(`/project/${projectId}/superadmin/get-projects-history`);
}

export function superadminGetOverallProjectsHealth() {
    return axios.get(`/project/${projectId}/superadmin/get-projects-health`);
}

export function superadminFindBy(productIds: string[], imageIds: string[], imageHases: string[], templateId: string, limit: number) {
    return axios.post(`/project/${projectId}/superadmin/find-by`, { productIds, imageIds, imageHases, templateId, limit });
}

export function superadminFindProjectBy(userEmail: string, userName: string, projectName: string, limit: number) {
    return axios.post(`/project/${projectId}/superadmin/find-project-by`, { userEmail, userName, projectName, limit });
}

export function superadminGetPromoTemplates() {
    return axios.get(`/project/${projectId}/superadmin/get-promo-templates`);
}

export function superadminSavePromoTemplates(promoTemplates: PromoTemplate[]) {
    return axios.post(`/project/${projectId}/superadmin/save-promo-templates`, { promoTemplates });
}

export function superadminGetProjectHealth(selectedProjectId: number, selectedTemplateId: number) {
    return axios.post(`/project/${projectId}/superadmin/get-project-health`, { selectedProjectId: selectedProjectId, selectedTemplateId: selectedTemplateId });
}

export function superadminGetQueuesData(lengthsAge: number, lengthsIncr: number) {
    return axios.get(`/project/${projectId}/superadmin/rabbit-queues/${lengthsAge}/${lengthsIncr}`)
}

export function superadminFilterQueue(originQueueName: string, destinationQueueName: string, whereKey: string, whereValue: number, action: QueueActionType) {
    return axios.post(`/project/${projectId}/superadmin/rabbit-filter-queue`, { originQueueName, destinationQueueName, whereKey, whereValue, action })
}

export function superadminGetSystemInfo(selectedProjectId: number, selectedTemplateId: number) {
    return axios.post(`/project/${projectId}/superadmin/system-info`, { selectedProjectId: selectedProjectId, selectedTemplateId: selectedTemplateId })
}

export function superadminGetProjectInfo(selectedProjectId: number, selectedTemplateId: number) {
    return axios.post(`/project/${projectId}/superadmin/project-info`, { selectedProjectId: selectedProjectId, selectedTemplateId: selectedTemplateId })
}

export function superadminGetShopInfo(selectedProjectId: number, shopId: number) {
    return axios.post(`/project/${projectId}/superadmin/shop-info`, { selectedProjectId: selectedProjectId, shopId: shopId })
}

export function superadminGetUserInfo(selectedProjectId: number, userId: number) {
    return axios.post(`/project/${projectId}/superadmin/user-info`, { selectedProjectId: selectedProjectId, userId: userId })
}

export function getFormatSpecification(formatName: string) {
    return axios.get(`/project/${projectId}/format-specification/get/${formatName}`)
}

export function getFormatNamesLike(searchString: string) {
    return axios.post(`/project/${projectId}/format-specification/autocomplete`, { searchString })
}

export function getAllFormatsSpecifications(page: number, limit: number, searchString: string) {
    return axios.post(`/project/${projectId}/format-specification/all/${page}/${limit}`, { searchString })
}

export function superadminGetTemplates(selectedProjectId: number = projectId) {
    return axios.get(`/project/${projectId}/superadmin/${selectedProjectId}/templates`)
}

export function superadminGetTemplatePreviews(selectedProjectId: number = projectId, page: number, limit: number, recentFirst: boolean) {
    return axios.post(`/project/${projectId}/superadmin/template-previews`, { selectedProjectId, page, limit, recentFirst })
}

export function superadminGetMergadoQueriesInfo(selectedProjectId: number = projectId, fields: string[], filter: MergadoFilterType[]) {
    return axios.post(`/project/${projectId}/superadmin/${selectedProjectId}/get-mergado-queries`, { fields, filter })
}

export function superadminGetMergadoRulesInfo(selectedProjectId: number = projectId, fields: string[], filter: MergadoFilterType[]) {
    return axios.post(`/project/${projectId}/superadmin/${selectedProjectId}/get-mergado-rules`, { fields, filter })
}

export function superadminGetProductImage(productId: number) {
    return axios.post(`/project/${projectId}/superadmin/product/${productId}`, {})
}

export function superadminGetProductIdsLike(searchString: string, selectedProjectId: number, selectedTemplateId: number) {
    return axios.post(`/project/${projectId}/superadmin/product/search`, { searchString: searchString, selectedProjectId: selectedProjectId, selectedTemplateId: selectedTemplateId })
}

export function superadminGetTemplatesIdsLike(searchString: string) {
    return axios.post(`/project/${projectId}/superadmin/template/search`, { searchString: searchString })
}

export function superadminGetProductImages(selectedProjectId: number, selectedTemplateId: number, selectedProductId: number, page: number, limit: number, getRandom: boolean, groupByImage: boolean, recentFirst: boolean, special: string) {
    return axios.post(`/project/${projectId}/superadmin/product/images`, { selectedProjectId, selectedTemplateId, selectedProductId, page, limit, getRandom, groupByImage, recentFirst, special })
}

export function superadminGetMergadoProducts(selectedProjectId: number, selectedTemplateId: number, selectedProductId: number, page: number, limit: number, version: string) {
    return axios.post(`/project/${projectId}/superadmin/mergado-products`, { selectedProjectId, selectedTemplateId, selectedProductId, page, limit, version })
}

export function superadminGetMergadoElements(selectedProjectId: number) {
    return axios.post(`/project/${projectId}/superadmin/mergado-elements`, { selectedProjectId })
}

export function superadminGetRandomImages(page: number, limit: number, getRandom: boolean, groupByImage: boolean, recentFirst: boolean, special: string) {
    return axios.post(`/project/${projectId}/superadmin/product/random-images`, { page, limit, getRandom, groupByImage, recentFirst, special })
}

export function superadminFillQueues(type: PublishType | 'validations', isAlt: boolean, selectedProjectId: number, selectedTemplateId: number, selectedProductIds: ID[]) {
    return axios.put(`/project/${projectId}/superadmin/fill-queues/${type}`, { type, isAlt, selectedProjectId, selectedTemplateId, selectedProductIds })
}

export function superadminClearDownloadCounter(isAlt: boolean, selectedProjectId: number, selectedProductIds: ID[]) {
    return axios.put(`/project/${projectId}/superadmin/clear-download-counter`, { isAlt, selectedProjectId, selectedProductIds })
}

export function superadminClearRenderCounter(isAlt: boolean, selectedProjectId: number, selectedProductIds: ID[]) {
    return axios.put(`/project/${projectId}/superadmin/clear-render-counter`, { isAlt, selectedProjectId, selectedProductIds })
}

export function superadminUnlinkTemplatePNGCache(selectedProjectId: ID, templateId: ID) {
    return axios.put(`/project/${projectId}/superadmin/unlink-template-png-cache`, { selectedProjectId, templateId })
}

export function superadminCleanImagesCache(selectedProjectId: ID, templateId: ID, isAlt: boolean) {
    return axios.put(`/project/${projectId}/superadmin/clean-images-cache`, { selectedProjectId, templateId, isAlt })
}

export function superadminClearSourceByType(selectedProjectId: ID, templateId: ID, type: TemplateTypes, isAlt: boolean) {
    return axios.put(`/project/${projectId}/superadmin/clear-source-by-type`, { selectedProjectId, templateId, type, isAlt })
}

export function superadminCreateHashListVisualSimilarity(selectedProjectId: ID, selectedTemplateId: ID) {
    return axios.put(`/project/${projectId}/superadmin/similarity-create-list`, { selectedProjectId, selectedTemplateId })
}

export function superadminToggleProjectLarge(selectedProjectId: number) {
    return axios.put(`/project/${projectId}/superadmin/toggle-project-large`, { selectedProjectId })
}

export function superadminToggleProjectSkip(selectedProjectId: number) {
    return axios.put(`/project/${projectId}/superadmin/toggle-project-skip`, { selectedProjectId })
}

export function superadminGetRedisTemplate(templateId: number) {
    return axios.get(`/project/${projectId}/superadmin/redis/template/${templateId}`)
}

export function superadminGetDbTemplate(templateId: number) {
    return axios.get(`/project/${projectId}/superadmin/db/template/${templateId}`)
}

export function superadminRedisRefreshTemplate(templateId: number) {
    return axios.get(`/project/${projectId}/superadmin/redis/refresh-template/${templateId}`)
}

export function superadminGetRedisKeys() {
    return axios.get(`/project/${projectId}/superadmin/redis/keys`)
}

export function superadminApplyRules(selectedProjectId: number) {
    return axios.post(`/project/${projectId}/superadmin/rules/start-apply`, { selectedProjectId })
}

export function setUserNewsBox(value) {
    return axios.put(`/project/${projectId}/settings/set-user-news-box/${value}`)
}

export function setPopUpBox() {
    return axios.put(`/project/${projectId}/settings/set-popup-box`)
}

export function checkProjectElements() {
    return axios.get(`/project/${projectId}/settings/check-project-elements`)
}

export function applySelectedElementChanges(selectedChanges: CheckElementsType) {
    return axios.post(`/project/${projectId}/settings/apply-selected-elements`, { selectedChanges })
}

export function applyAllSuggestedElementChanges() {
    return axios.get(`/project/${projectId}/settings/apply-all-suggested-elements`)
}

export function setFirstStepsWizard(value) {
    return axios.put(`/project/${projectId}/settings/set-first-steps-wizard/${value}`)
}

export function saveSettingsOrder(settingsOrder: string) {
    return axios.put(`/project/${projectId}/settings/save-order`, { settingsOrder: settingsOrder })
}

export function saveProjectSettings(settings: SettingsFormType) {
    return axios.put(`/project/${projectId}/settings/save`, settings)
}

export function getRandomPreviewImage(outputFormat, outputCompression = 95, productId = null) {
    return axios.get(`/project/${projectId}/settings/random-preview-image?output_image_format=${outputFormat || 'png'}&output_image_compression=${outputCompression || 95}&product_id=${productId}`)
}

export function getHidingFilters() {
    return axios.get(`/project/${projectId}/settings/hiding-filters`)
}

export function getAskBeforeLeave() {
    return axios.get(`/project/${projectId}/settings/is-ask-before-leave`)
}

export function getGroupByImage() {
    return axios.get(`/project/${projectId}/settings/is-group-by-image`)
}

export function getProductsPerPageOnList() {
    return axios.get(`/project/${projectId}/settings/products-per-page-on-list`)
}

export function deleteProjectSettings() {
    return axios.delete(`/project/${projectId}/settings/delete`)
}

export function getProject() {
    return axios.get(`/project/${projectId}`)
}

export function getTemplates() {
    return axios.get(`/project/${projectId}/templates`)
}

export function getTemplate(templateId: ID) {
    return axios.get(`/project/${projectId}/templates/${templateId}`)
}

export function getAltTemplatesIds(templateId: ID) {
    return axios.get(`/project/${projectId}/alternative-templates/${templateId}/get-ids`)
}

export function getAlternativeTemplates() {
    return axios.get(`/project/${projectId}/alternative-templates`)
}

export function getAlternativeTemplatesLevels() {
    return axios.get(`/project/${projectId}/alternative-templates/levels`)
}

export function getAlternativeTemplate(alternativeTemplateId: ID) {
    return axios.get(`/project/${projectId}/alternative-templates/${alternativeTemplateId}`)
}

export function reorderAlternativeTemplates(orderedAltTemplateIds: ID[]) {
    return axios.put(`/project/${projectId}/alternative-templates/reorder`, { orderedAltTemplateIds })
}

export function renameAlternativeTemplate(alternativeTemplateId: ID, newName: string) {
    return axios.post(`/project/${projectId}/alternative-templates/${alternativeTemplateId}/rename`, { newName })
}

export function deleteAlternativeTemplate(alternativeTemplateId: ID) {
    return axios.delete(`/project/${projectId}/alternative-templates/${alternativeTemplateId}`)
}

export function duplicateAlternativeTemplate(alterantiveTemplateId: ID) {
    return axios.post(`/project/${projectId}/alternative-templates/${alterantiveTemplateId}/duplicate`)
}

export function startOrPauseAlternativeTemplate(alternativeTemplate: AlternativeTemplate) {
    return axios.put(`/project/${projectId}/alternative-templates/${alternativeTemplate.id}/startorpause`)
}

export function pauseAlternativeTemplate(alternativeTemplate: Template) {
    return axios.put(`/project/${projectId}/alternative-templates/${alternativeTemplate.id}/pause`)
}

export function startAlternativeTemplate(alternativeTemplate: Template) {
    return axios.put(`/project/${projectId}/alternative-templates/${alternativeTemplate.id}/start`)
}

export function getAlternativeTemplateProgress(alternativeTemplateId: ID) {
    return axios.get(`/project/${projectId}/alternative-templates/${alternativeTemplateId}/progress`)
}

export function rerenderAlternativeTemplate(alternativeTemplateId: ID) {
    return axios.put(`/project/${projectId}/alternative-templates/${alternativeTemplateId}/rerender`)
}

export function redownloadAlternativeTemplate(alternativeTemplateId: ID) {
    return axios.put(`/project/${projectId}/alternative-templates/${alternativeTemplateId}/redownload`)
}

export function changeBindings(alternativeTemplateId: ID, newMainTemplateIds: ID[]) {
    return axios.post(`/project/${projectId}/alternative-templates/${alternativeTemplateId}/change-bindings`, { newMainTemplateIds })
}

export function changeLevelsAndMode(alternativeTemplateId: ID, templateId: ID, levelsMode: string, levels: string) {
    return axios.post(`/project/${projectId}/alternative-templates/${alternativeTemplateId}/levels-and-mode`, { templateId, levelsMode, levels })
}

export function getTemplateProgress(templateId: ID) {
    return axios.get(`/project/${projectId}/templates/${templateId}/progress`)
}

export function rerenderTemplate(templateId: ID) {
    return axios.put(`/project/${projectId}/templates/${templateId}/rerender`)
}

export function redownloadTemplate(templateId: ID) {
    return axios.put(`/project/${projectId}/templates/${templateId}/redownload`)
}

export function saveTemplate(template: TemplateOrAlternativeTemplateType, templateId: ID, altTemplateId: ID, isAlt: boolean, levels: string[], levelsMode: LevelsModeType) {
    return axios.put(`/project/${projectId}/templates/${template.id}`, {
        ...template,
        isAlt,
        levels,
        levelsMode,
        templateId,
        altTemplateId
    });
}

export function renameTemplate(templateId: ID, newName: string) {
    return axios.post(`/project/${projectId}/templates/${templateId}/rename`, { newName })
}

export function deleteTemplate(template: Template) {
    return axios.delete(`/project/${projectId}/templates/${template.id}`)
}

export function startOrPauseTemplate(template: Template) {
    return axios.put(`/project/${projectId}/templates/${template.id}/startorpause`)
}

export function pauseTemplate(template: Template) {
    return axios.put(`/project/${projectId}/templates/${template.id}/pause`)
}

export function startTemplate(template: Template) {
    return axios.put(`/project/${projectId}/templates/${template.id}/start`)
}

export function reorderTemplates(order: string[]) {
    return axios.put(`/project/${projectId}/templates/reorder`, { templates: order })
}

export function toggleReorderTemplatesAutomatically(isEnabled: intboolean) {
    return axios.put(`/project/${projectId}/templates/toggle-auto-priority`, { isEnabled })
}

export function updateTemplateFieldValue(templateId: ID, updateValues: object) {
    return axios.put(`/project/${projectId}/templates/${templateId}/update-field-value`, { updateValues })
}

export function duplicateTemplate(templateId) {
    return axios.post(`/project/${projectId}/templates/${templateId}/duplicate`)
}

export function exportTemplate(templateIds: number[], isAlt: boolean) {
    if (isAlt) {
        return axios.put(`/project/${projectId}/alternative-templates/export`, { templates: templateIds })
    } else {
        return axios.put(`/project/${projectId}/templates/export`, { templates: templateIds })
    }
}

export function importTemplate(file: string, fileName: string, isAlt: boolean) {
    if (isAlt) {
        return axios.put(`/project/${projectId}/alternative-templates/import`, { file, fileName })
    } else {
        return axios.put(`/project/${projectId}/templates/import`, { file, fileName })
    }
}

export function getAlternativeTemplateExportStatus(exportId: ID) {
    return axios.get(`/project/${projectId}/alternative-templates/export/${exportId}`)
}

export function getQueries() {
    return axios.get(`/project/${projectId}/queries`)
}

export function getFontsUsedInProject() {
    return axios.get(`/project/${projectId}/get-used-fonts`)
}

//template previews
export function getRandomImages(previewsForm: PreviewsForm) {
    const active = previewsForm.view.active || 0
    const page = previewsForm.view.page || 1
    const itemsPerPage = previewsForm.view.itemsPerPage || DEFAULT_PRODUCTS_PER_PAGE
    const randomNumber = previewsForm.randomNumber || new Date().getTime()

    return axios.get(`/project/${projectId}/images?active=${active}&page=${page}&randomNumber=${randomNumber}&itemsPerPage=${itemsPerPage}`)
}

export function getSizeImages(previewsForm: PreviewsForm) {
    const sizeId = previewsForm.view.id
    const page = previewsForm.view.page || 1
    const active = previewsForm.view.active || 0
    const itemsPerPage = previewsForm.view.itemsPerPage || DEFAULT_PRODUCTS_PER_PAGE
    const randomNumber = previewsForm.randomNumber || new Date().getTime()

    return axios.get(`/project/${projectId}/images/size/${sizeId}?page=${page}&active=${active}&randomNumber=${randomNumber}&itemsPerPage=${itemsPerPage}`)
}

export function getTemplateImages(previewsForm: PreviewsForm) {
    const templateId = previewsForm.view.id
    const page = previewsForm.view.page || 1
    const itemsPerPage = previewsForm.view.itemsPerPage || DEFAULT_PRODUCTS_PER_PAGE
    const randomNumber = previewsForm.randomNumber || new Date().getTime()

    return axios.get(`/project/${projectId}/images/template/${templateId}?page=${page}&randomNumber=${randomNumber}&itemsPerPage=${itemsPerPage}`)
}

export function svgRescale(svg: string, width: number, height: number) {
    return axios.put(`/project/${projectId}/svg/rescale`, {
        svg: svg,
        width: width,
        height: height,
    })
}

export function getRawSvg(pathOrBlob: string) {
    return axios.post(`/project/${projectId}/svg/get-raw`, { pathOrBlob })
}

export function getRandomProduct(form: TemplateOrAlternativeTemplateType, queries: ID[], templateId: ID, preferredRandomProductId: ID = null, alternativeTemplateId: ID = null, levels: string[] = null, levelsMode: LevelsModeType) {
    return axios.post(`/project/${projectId}/image/get-random-product`, { form, queries, templateId, preferredRandomProductId, alternativeTemplateId, levels, levelsMode })
}

export function applyEffectsToRandomProduct(randomProduct: RandomProduct, form: TemplateOrAlternativeTemplateType, templateId: ID, altLevels: string[], levelsMode: LevelsModeType, isAlt: boolean) {
    return axios.post(`/project/${projectId}/image/apply-effects`, { randomProduct, templateId, form, isAlt, altLevels, levelsMode })
}

export function getBgColorsLike(searchString: string) {
    return axios.post(`/project/${projectId}/image/get-bgcolors-like`, { searchString: searchString })
}

export function getSizes() {
    return axios.get(`project/${projectId}/sizes`)
}

export function getImagickFonts() {
    return axios.post(`/imagick-fonts`, { projectId })
}

export function getShapeSets() {
    return axios.get(`project/${projectId}/shape-sets`)
}

export function getCustomImageSets() {
    return axios.get(`/project/${projectId}/customsets`)
}

export function getCustomImageSet(setId: ID) {
    return axios.get(`/project/${projectId}/customsets/${setId}`)
}

export function customImageSetFileGetImageBlob(fileId: ID, isRemoveBackground: intboolean, shapeIndex: string) {
    return axios.post(`/project/${projectId}/customsets/imageBlob`, { fileId, isRemoveBackground, shapeIndex })
}

export function imageFromElementsGetImageBlob(randomProductId: ID, imageUrl: string, isRemoveBackground: intboolean, shapeIndex: string) {
    return axios.post(`/project/${projectId}/image-from-elements/imageBlob`, { randomProductId, imageUrl, isRemoveBackground, shapeIndex })
}

export function getAllCustomImageSetFiles() {
    return axios.get(`/project/${projectId}/customsets/get`)
}

export function downloadAllCustomImageSetFiles() {
    return axios.post(`/project/${projectId}/customsets/download-all`)
}

export function getGradients() {
    return axios.get(`/project/${projectId}/gradients`)
}

export function saveGradient(gradient: Gradient) {
    return axios.put(`/project/${projectId}/gradients/save`, { gradient })
}

export function exportGradients(gradientsIds: ID[]) {
    return axios.put(`/project/${projectId}/gradients/export`, { gradientsIds })
}

export function importGradients(base64: string, sourceName: string) {
    return axios.put(`/project/${projectId}/gradients/import`, { base64, sourceName })
}

export function deleteGradient(gradientId: ID) {
    return axios.delete(`/project/${projectId}/gradients/delete/${gradientId}`)
}

export function getSVGColors(svg: string) {
    return axios.put(`/project/${projectId}/palettes/get-svg-colors`, { svg: svg })
}

export function replaceSVGColor(svg: string, search: string, replace: string, nth: number = -1) {
    return axios.put(`/project/${projectId}/palettes/replace-svg-color`, { svg: svg, search: search, replace: replace, nth: nth })
}

export function getDefaultPalettes() {
    return axios.get(`/project/${projectId}/palettes`)
}

export function getCustomPalettes() {
    return axios.get(`/project/${projectId}/palettes/get-custom`)
}

export function saveCustomPalettes(customPalettesForm: CustomPalettesFormType) {
    return axios.put(`/project/${projectId}/palettes/save-custom-palettes`, customPalettesForm)
}

export function saveDefaultPaletteId(paletteId: ID) {
    return axios.put(`/project/${projectId}/palettes/save-palette-id`, { paletteId: paletteId })
}

export function saveCustomPaletteId(customPaletteId: ID) {
    return axios.put(`/project/${projectId}/palettes/save-custom-palette-id`, { customPaletteId: customPaletteId })
}

export function getPaletteFromImageString(imageString: string, width: number, height: number, contentType: string, origName: string) {
    return axios.post(`/project/${projectId}/palettes/from-file`, { image: imageString, width, height, contentType, origName })
}

export function createCustomImageSet(setName: string) {
    return axios.put(`/project/${projectId}/customsets`, { setName: setName })
}

export function deleteCustomImageSetFile(setFile: CustomImageSetFile) {
    return axios.delete(`/project/${projectId}/customsets/${setFile.set_id}/${setFile.id}`)
}

export function updateCustomImageSetFileSourceInfo(setFile: CustomImageSetFile, sourceInfo: Pick<CustomImageSetFile, 'source_url' | 'license' | 'author'>) {
    return axios.patch(`/project/${projectId}/customsets/${setFile.set_id}/${setFile.id}/source-info`, sourceInfo)
}

export function deleteCustomImageSet(customSet: CustomImageSet) {
    return axios.delete(`/project/${projectId}/customsets/${customSet.id}`)
}

export function saveCustomImageFile(shape: ShapeType, origName: string) {
    return axios.put(`/project/${projectId}/customsets/savefile`, { shape, origName })
}

export function postRenderPreviewImage(requestData: any, templateId: ID, alternativeTemplateId: ID, levels: string[], levelsMode: LevelsModeType, queries: string[]) {
    return axios.post(`/project/${projectId}/render-preview-image`, { requestData, templateId, alternativeTemplateId, levels, levelsMode, queries })
}

export function getTemplateDiagnostics(active: intboolean, templateName: string) {
    return axios.post(`/project/${projectId}/diagnostics/template-diagnostics`, { active, templateName })
}

export function getAltTemplateDiagnostics(active: intboolean, templateName: string) {
    return axios.post(`/project/${projectId}/diagnostics/alt-template-diagnostics`, { active, templateName })
}

export function getProblems() {
    return axios.post(`/project/${projectId}/diagnostics/get-problems`)
}

export function getWarnings() {
    return axios.post(`/project/${projectId}/diagnostics/get-warnings`)
}

export function getRecommendations() {
    return axios.post(`/project/${projectId}/diagnostics/get-recommendations`)
}

export function getOtherDiagnostics() {
    return axios.post(`/project/${projectId}/diagnostics/get-other`)
}

export function getDynamicElementsAvailability(filters: ProductWithoutElementFilter) {
    return axios.post(`/project/${projectId}/diagnostics/elements-availability/${filters.page ?? 0}`, { filters })
}

export function getAvailableOptions() {
    return axios.get(`/project/${projectId}/products/get-available-options`)
}

export function getProducts(filters: FiltersFormType, onlyProductIds: ID[] = [], downloadImagesIfNotDownloaded = false, onlyImageHashes: string[] = null) {
    return axios.post(`/project/${projectId}/products`, { filters, onlyProductIds, downloadImagesIfNotDownloaded, onlyImageHashes })
}

export function getGroupCounts() {
    return axios.get(`/project/${projectId}/products/get-group-counts`)
}

export function createProductsExport(filters: FiltersFormType, viewtable: ViewTableType | 'row') {
    return axios.post(`/project/${projectId}/products/export-to-csv`, { filters, viewtable })
}

export function getProductsExport(exportId: ID) {
    return axios.get(`/project/${projectId}/products/export/${exportId}`)
}

export function getTemplateExport(exportId: ID) {
    return axios.get(`/project/${projectId}/templates/export/${exportId}`)
}

export function getSimilarityExport(exportId: ID) {
    return axios.get(`/project/${projectId}/visual-similarity/export/${exportId}`)
}

export function getExistingSimilarityResult(imageHash: string, n: number, threshold: number, templateId: ID, templateids: ID[]) {
    return axios.post(`/project/${projectId}/visual-similarity/get-result`, { imageHash, n, threshold, templateId, templateids })
}

export function visualSimilarityFullExportFindSimilar(imageHash: string, file: FileInputType = null) {
    return axios.post(`/project/${projectId}/visual-similarity/full-export-get-similar`, { imageHash, file })
}

export function getTemplateGallery(sizeRatio?: number, filterGroup?: string, limit?: number, randomImageId?: number) {
    return axios.post(`/project/${projectId}/templates/gallery`, { sizeRatio, filterGroup, limit, randomImageId })
}

export function getProductGallery(columnsPerRow: number) {
    return axios.post(`/project/${projectId}/templates/product-gallery`, { columnsPerRow })
}

export function loadTemplateGallery(id: number) {
    return axios.put(`/project/${projectId}/templates/gallery/load`, { id })
}

export function getPreviews(previewsForm: PreviewsForm) {
    if (previewsForm.view.type === "size" && previewsForm.view.id !== "ALL") {
        return getSizeImages(previewsForm)
    } else if (previewsForm.view.type === "template") {
        return getTemplateImages(previewsForm)
    }
    return getRandomImages(previewsForm)
}

export function setValidationByUser(imageId: ID, value: SetByUser) {
    return axios.put(`/project/${projectId}/validations/set-validation/${imageId}`, { value: value })
}

export function setWatermarkByUser(imageId: ID, value: SetByUser) {
    return axios.put(`/project/${projectId}/watermarks/set-watermark/${imageId}`, { value: value })
}

export function setQualityByUser(imageId: ID, value: SetByUser) {
    return axios.put(`/project/${projectId}/image-quality/set-quality/${imageId}`, { value: value })
}

export function clearOldProductsFromDb() {
    return axios.delete(`/project/${projectId}/products/clear-old`)
}

export function saveFilterAsQuery(filtersForm: FiltersFormType, queryName: string, queryElement: string, viewTable: ViewTableType, isActive: intboolean | null = null, queryId: ID = null) {
    return axios.put(`/project/${projectId}/fie-query/save`, { filtersForm, queryName, queryElement, viewTable, isActive, queryId })
}

export function getAllFIEQueries() {
    return axios.get(`/project/${projectId}/fie-query/get-all`)
}

export function deleteFIEQuery(fieQueryId: ID) {
    return axios.delete(`/project/${projectId}/fie-query/${fieQueryId}/delete`)
}

export function toggleActiveFIEQuery(fieQueryId: ID) {
    return axios.put(`/project/${projectId}/fie-query/${fieQueryId}/toggle-active`)
}

export function createPredefinedFIEQueries(elements: string[]) {
    return axios.put(`/project/${projectId}/fie-query/create-predefined`, { elements })
}

export function getPredefinedFIEQueries() {
    return axios.get(`/project/${projectId}/fie-query/get-predefined`)
}

export function checkAllPredefinedExists() {
    return axios.get(`/project/${projectId}/fie-query/check-all-predefined-exists`)
}

export function getRemovedWatermarkImageBlob(productId: ID, imageOrAltImageId: ID, altLevel?: number) {
    if (altLevel === null) {
        return axios.get(`/project/${projectId}/watermarks/result/${productId}/${imageOrAltImageId}`)
    } else {
        return axios.get(`/project/${projectId}/watermarks/result/${productId}/${imageOrAltImageId}/${altLevel}`)
    }
}

export function getInpaintedImageBlob(productId: ID, imageId: ID, groupId: ID) {
    return axios.get(`/project/${projectId}/inpainting/result/${productId}/${groupId}/${imageId}`)
}

export function saveWatermarkObjects(isAlt: boolean, imageOrAltImageId: ID, altLevel: number, productId: ID, userSetObjects: ObjectsType, pngBase64Url: string, width: number, height: number) {
    return axios.put(`/project/${projectId}/watermarks/save-objects`, { imageOrAltImageId, altLevel, productId, userSetObjects, pngBase64Url, width, height, isAlt })
}

export function saveInpaintingObjects(isAlt: boolean, groupId: ID, userSetObjects: ObjectsType, pngBase64Url: string, width: number, height: number, productId: ID, templateId: ID, altTemplateId: ID, imageOrAltId: ID, imageOrAltUrl: string, position: PositionType, scaleObjects: boolean) {
    console.debug('save inpainting objects', { isAlt, width, height, groupId, userSetObjects, pngBase64Url, productId, templateId, altTemplateId, imageOrAltId, imageOrAltUrl, position, scaleObjects })
    return axios.put(`/project/${projectId}/inpainting/save-objects`, { groupId, userSetObjects, pngBase64Url, width, height, productId, templateId, altTemplateId, imageOrAltId, imageOrAltUrl, position, scaleObjects, isAlt })
}

export function setInpaintingGroup(group: InpaintingGroupType) {
    return axios.put(`/project/${projectId}/inpainting/set-group`, { group })
}

export function deleteInpaintingGroup(groupId: ID) {
    return axios.delete(`/project/${projectId}/inpainting/delete/${groupId}`)
}

export function saveInvertMaskObjects(imageOrAltImageId: ID, imageOrAltImageHash: string, userSetObjects: ObjectsType, mode: InvertMaskModeType, width: number, height: number, isAlt: boolean) {
    return axios.put(`/project/${projectId}/bgremove/save-objects`, { imageOrAltImageId, imageOrAltImageHash, userSetObjects, mode, width, height, isAlt })
}

export function getInpaintingGroups() {
    return axios.get(`/project/${projectId}/inpainting/get-masks`)
}

export type PublishType = 'downloader' | 'bgremover' | 'composer' | 'upscaler' | 'autowmremover' | 'outpainter' | 'inpainter'
export function publishProducts(productIds: ID[], type: PublishType, pid: ID = null) {
    return axios.put(`/project/${projectId}/products/publish/${type}`, { productIds: productIds, pid })
}

export function selectUpscalePreviewImages(queries: ID[], page: number) {
    return axios.post(`/project/${projectId}/upscale/select-preview-images`, { queries: queries, page: page })
}

export function getUpscaleProgress() {
    return axios.get(`/project/${projectId}/upscale/progress`)
}

export function getMergadoElements(allowHidden: boolean = true, recursive: boolean = true, excludeOrigins: string[] = null) {
    return axios.post(`/project/${projectId}/mergado/elements`, { allowHidden, recursive, excludeOrigins })
}

export function getMergadoElementsSize() {
    return axios.post(`/project/${projectId}/mergado/elements-size`)
}

export function getNecessaryElementsForFIE() {
    return axios.get(`/project/${projectId}/mergado/get-necessary-elements`)
}

export function getMergadoProducts(offset: number = 0) {
    return axios.get(`/project/${projectId}/mergado/products/${offset}`)
}

export function saveProductElementsMap(project: Project) {
    return axios.put(`/project/${projectId}/settings/save-elements-map`, { project: project })
}

export function saveAdvancedSettings(maxPayloadSize: number, elementRegexp: string, xmlEquivalent: string[], applyElements: ApplyElementsType) {
    return axios.put(`/project/${projectId}/settings/save-advanced`, { maxPayloadSize, elementRegexp, xmlEquivalent, applyElements })
}

export function setDisableShowOverlap(disable_show_overlap: boolean) {
    return axios.put(`/project/${projectId}/settings/set-disable-show-overlap`, { disable_show_overlap: disable_show_overlap })
}

export function setByComparisonEngine(comparisonEngine: ComparisonEngineType) {
    return axios.put(`/project/${projectId}/settings/set-by-comparison-engine`, { comparisonEngine: comparisonEngine })
}

export function getProjectInfo() {
    return axios.get(`/project/${projectId}/settings/get-project-info`)
}

export function getComparisonEngines() {
    return axios.get(`/project/${projectId}/settings/get-comparison-engines`)
}

export function getWatermarkGroups() {
    return axios.get(`/project/${projectId}/watermark-groups`)
}

export function createNewWatermarkGroup(groupName: string) {
    return axios.post(`/project/${projectId}/watermark-groups/new`, { groupName })
}

export function getProductsForWatermarkGroupsFromFie(templateId: ID, watermarkGroupId: number, queryIds: string, page: number, limit: number, withMask: intboolean, withWatermarks: intboolean, isAlt: boolean, levels: string[], levelsMode: LevelsModeType, randomProductId: number) {
    return axios.post(`/project/${projectId}/watermark-groups/get-products-fie`, { templateId, watermarkGroupId, queryIds, page, withMask, limit, withWatermarks, isAlt, levels, levelsMode, randomProductId })
}

export function getProductsForWatermarkGroupsFromMergado(templateId: ID, watermarkGroupId: number, queryIds: string, page: number, limit: number, isAlt: boolean, levels: string[], levelsMode: LevelsModeType, randomProductId: number) {
    return axios.post(`/project/${projectId}/watermark-groups/get-products-mergado`, { templateId, watermarkGroupId, queryIds, limit, page, isAlt, levels, levelsMode, randomProductId })
}

export function addImagesToWatermarksGroup(productList: WMProductItemType[], watermarkGroupId: number) {
    return axios.post(`/project/${projectId}/watermark-groups/add-images`, { productList, watermarkGroupId })
}

export function saveGroupImageWatermarkObjects(watermark_group_image_id: ID, user_set_objects: ObjectsType) {
    return axios.put(`/project/${projectId}/watermark-groups/save-objects`, { watermark_group_image_id, user_set_objects })
}

export function deleteWatermarkGroupImage(watermark_group_image_id: ID, watermark_group_id: ID) {
    return axios.delete(`/project/${projectId}/watermark-groups/images/${watermark_group_id}/${watermark_group_image_id}/delete`)
}

export function deleteWatermarkGroup(watermark_group_id: ID) {
    return axios.delete(`/project/${projectId}/watermark-groups/images/${watermark_group_id}/delete`)
}

export function publishWatermarkGroup(watermark_group_id: ID, force: boolean) {
    return axios.post(`/project/${projectId}/watermark-groups/${watermark_group_id}/publish`, { force })
}

export function publishProductToWatermarkDetector(watermark_group_id: ID, productId: ID) {
    return axios.post(`/project/${projectId}/watermark-groups/${watermark_group_id}/publish-product/${productId}`)
}

export function publishImageToWatermarkDetector(watermark_group_id: ID, imageId: ID) {
    return axios.post(`/project/${projectId}/watermark-groups/${watermark_group_id}/publish-image/${imageId}`)
}

export function publishTemplateToWatermarkDetector(watermark_group_id: ID, templateId: ID) {
    return axios.post(`/project/${projectId}/watermark-groups/${watermark_group_id}/publish-template/${templateId}`)
}

export function renameWatermarkGroup(watermark_group_id: ID, newName: string) {
    return axios.post(`/project/${projectId}/watermark-groups/${watermark_group_id}/rename`, { newName })
}

export function getRemoveWatermarkDetectorResults(watermark_group_id: ID, page: number, limit: number, filter: WMResultsFilterType) {
    return axios.post(`/project/${projectId}/watermark-groups/${watermark_group_id}/results-from-detector`, { page, limit, filter })
}

export function downloadRemoveWatermarkResults(watermark_group_id: ID) {
    return axios.post(`/project/${projectId}/watermark-groups/${watermark_group_id}/download-results`)
}

export function visualSimilarityCreateHashListAndPublish(templateId: ID, templateIds: ID[], productId: ID, nLimit: number, threshold: number, forcePriority: boolean = null) {
    return axios.post(`/project/${projectId}/visual-similarity/export-and-publish`, { templateId, templateIds, productId, nLimit, threshold, forcePriority })
}

export function addSubscriber(email: string) {
    return axios.post(`/project/${projectId}/newsletter/add-subscriber`, { email })
}

export function saveContactEmail(email: string) {
    return axios.post(`/project/${projectId}/newsletter/save-contact-email`, { email })
}

export function getBrushPatternCursor(pattern: string, patternColor: string, radius: number) {
    return axios.post(`/project/${projectId}/brush-pattern/get-cursor`, { pattern, patternColor, radius })
}

export function searchExternalImage(search: string, limit: number, start: number) {
    return axios.post(`/project/${projectId}/external-image/search`, { search, limit, start })
}

export function getExternalImageCollections() {
    return axios.post(`/project/${projectId}/external-image/get-collections`)
}

export function getExternalImageCollection(prefix: string = null) {
    return axios.post(`/project/${projectId}/external-image/get-collection/${prefix}`)
}

export function saveImageAsCustomSetFile(icon: string, shape: ShapeType, width: number, height: number, color: string, origFileName: string, svg?: string) {
    return axios.put(`/project/${projectId}/external-image/save-image`, { icon, shape, width, height, color, origFileName, svg })
}

export function getProjectsOfUser() {
    return axios.post(`/project/${projectId}/projects-of-user/get`)
}

export function getProjectImportPreviews(fromProjectId: number) {
    return axios.post(`/project/${projectId}/projects-of-user/previews`, { fromProjectId })
}

export function importProjectToProject(fromProjectId: number, importOptions: ProjectToProjectImportOption[]) {
    return axios.put(`/project/${projectId}/projects-of-user/import-from-project`, { fromProjectId, importOptions })
}

export function checkDepotsEnabled() {
    return axios.post(`/project/${projectId}/is-depots-enabled`)
}

export function MCP_call(method: 'tools/call' | 'tools/list' | 'resources/list' | 'resources/read' | 'initialize', args: Object, name: string) {
    return axios.post(`/mcp`, {
        jsonrpc: '2.0',
        id: 1,
        method: method,
        params: {
            name: name,
            arguments: args,
            protocolVersion: '2025-06-18',
            clientInfo: { name: 'fie', version: '1.0.0' },
        },
    })
}

export function MCP_readResource(uri: string, args: Object) {
    return axios.post(`/mcp`, {
        jsonrpc: '2.0',
        id: 1,
        method: 'resources/read',
        params: {
            uri: uri,
            arguments: args,
            protocolVersion: '2025-06-18',
            clientInfo: { name: 'fie', version: '1.0.0' },
        },
    })
}